import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Avatar, IconButton, Chip, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Snackbar, Alert } from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import api from '../../api';

// Import Child Components
import ProfilePersonal from './ProfilePersonal';
import ProfileOfficial from './ProfileOfficial';
import ProfileEducation from './ProfileEducation';
import ProfileAccount from './ProfileAccount';
import ProfileDocuments from './ProfileDocuments';

const MyProfile = () => {
  const rawEmpId = localStorage.getItem('emp_id') || '';
  const empId = rawEmpId.trim().toUpperCase();
  
  const [empData, setEmpData] = useState({});
  const [activeMenu, setActiveMenu] = useState('personal');
  
  const [documents, setDocuments] = useState([]);
  const [docCategories, setDocCategories] = useState([]);
  const [deps, setDeps] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [eduLevels, setEduLevels] = useState([]);
  const [expRanges, setExpRanges] = useState([]);
  const [shifts, setShifts] = useState([]);

  const [snackbar, setSnackbar] = useState({ open: false, msg: '', type: 'success' });
  const showMsg = (msg, type = 'success') => setSnackbar({ open: true, msg, type });
  const [photoStamp, setPhotoStamp] = useState(Date.now());

  const baseUrl = api.defaults.baseURL || `http://${window.location.hostname}:8000/api/v1`;
  const profilePicUrl = `${baseUrl}/employees/${empId}/profile-picture?t=${photoStamp}`;

  useEffect(() => {
    if (!empId) return;
    api.get(`/employees/${empId}`).then(res => setEmpData(res.data || {})).catch(console.warn);
    api.get(`/employees/${empId}/documents`).then(res => setDocuments(res.data || [])).catch(console.error);

    Promise.all([
      api.get('/settings/departments').catch(() => ({ data: [] })),
      api.get('/settings/designations').catch(() => ({ data: [] })),
      api.get('/myprofile/lookups/education-levels').catch(() => ({ data: [] })),
      api.get('/myprofile/lookups/experience-ranges').catch(() => ({ data: [] })),
      api.get('/settings/document_categories').catch(() => ({ data: [] })),
      api.get('/settings/shifts').catch(() => ({ data: [] }))
    ]).then(res => {
      setDeps(res[0].data); setDesigs(res[1].data); setEduLevels(res[2].data);
      setExpRanges(res[3].data); setDocCategories(res[4].data); setShifts(res[5].data);
    });
  }, [empId]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/employees/${empId}/profile-picture`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      showMsg("Profile picture updated!");
      setPhotoStamp(Date.now());
    } catch (err) { showMsg("Failed to update picture", "error"); }
  };

  const NavItem = ({ id, label, icon }) => (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton 
        selected={activeMenu === id} 
        onClick={() => setActiveMenu(id)} 
        sx={{ 
          borderRadius: '8px', 
          mx: 1, 
          '&.Mui-selected': { 
            bgcolor: 'primary.main', 
            color: 'white', 
            '&:hover': { bgcolor: 'primary.dark' }, 
            '& .MuiListItemIcon-root': { color: 'white' } 
          } 
        }}
      >
        <ListItemIcon sx={{ minWidth: 40, color: activeMenu === id ? 'white' : 'inherit' }}>
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={label} 
          primaryTypographyProps={{ 
            fontWeight: activeMenu === id ? 'bold' : 'medium', 
            fontSize: '0.85rem' 
          }} 
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Paper elevation={3} sx={{ display: 'flex', minHeight: '85vh', mt: 1, width: '100%', borderRadius: 3, overflow: 'hidden' }}>
      
      {/* Left Sidebar - Profile Card & Navigation */}
      <Box sx={{ 
        width: 225, 
        borderRight: 1, 
        borderColor: 'divider', 
        bgcolor: '#ffffff', 
        pt: 2, 
        pb: 2, 
        boxShadow: '2px 0 5px rgba(0,0,0,0.05)', 
        zIndex: 1, 
        flexShrink: 0, 
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Profile Card */}
        <Box sx={{ px: 2, mb: 2 }}>
          <Paper variant="outlined" sx={{ 
            borderRadius: 2, 
            borderColor: '#E2E8F0', 
            overflow: 'hidden', 
            textAlign: 'center', 
            bgcolor: '#ffffff'
          }}>
            <Box sx={{ height: 70, bgcolor: 'primary.main' }} />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: -5, position: 'relative', mb: 1 }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar 
                  src={profilePicUrl} 
                  sx={{ 
                    width: 90, 
                    height: 90, 
                    border: '4px solid white', 
                    bgcolor: '#fff', 
                    boxShadow: '0 2px 12px rgba(0,0,0,0.1)' 
                  }} 
                />
                <IconButton 
                  component="label" 
                  sx={{ 
                    position: 'absolute', 
                    bottom: 2, 
                    right: 2, 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    width: 28, 
                    height: 28, 
                    '&:hover': { bgcolor: 'primary.dark' } 
                  }}
                >
                  <PhotoCamera sx={{ fontSize: 14 }} />
                  <input hidden accept="image/*" type="file" onChange={handlePhotoUpload} />
                </IconButton>
              </Box>
            </Box>
            <Box sx={{ p: 2, pt: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {empData.first_name || 'My'} {empData.last_name || 'Profile'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, display: 'block', mb: 1 }}>
                {desigs.find(d => d.id === empData.designation_id)?.name || '--'}
              </Typography>
              <Chip 
                label={empData.status || 'ACTIVE'} 
                color={empData.status === 'ACTIVE' ? "success" : "default"} 
                size="small" 
                sx={{ fontWeight: 'bold', fontSize: '0.65rem' }} 
              />
            </Box>
          </Paper>
        </Box>

        {/* Navigation */}
        <List component="nav" dense sx={{ py: 0, flex: 1 }}>
          <NavItem id="personal" label="Personal Details" icon={<PersonOutlineIcon fontSize="small" />} />
          <NavItem id="official" label="Official Information" icon={<WorkOutlineIcon fontSize="small" />} />
          <NavItem id="education" label="Education & Skills" icon={<MenuBookIcon fontSize="small" />} />
          <NavItem id="account" label="Account Details" icon={<AccountBalanceWalletIcon fontSize="small" />} />
          <NavItem id="documents" label="My Documents" icon={<FolderOpenIcon fontSize="small" />} />
          <NavItem id="payroll" label="Payroll Details" icon={<ReceiptLongIcon fontSize="small" />} />
        </List>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, p: 4, width: '100%', bgcolor: '#fbfbfb', overflowY: 'auto', minWidth: 0 }}>
        <Paper elevation={2} sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, minHeight: '75vh', p: 3 }}>
          
          {/* Conditional Rendering of Child Components */}
          {activeMenu === 'personal' && <ProfilePersonal empData={empData} />}
          {activeMenu === 'official' && <ProfileOfficial empData={empData} deps={deps} desigs={desigs} shifts={shifts} />}
          {activeMenu === 'education' && <ProfileEducation empData={empData} eduLevels={eduLevels} expRanges={expRanges} />}
          {activeMenu === 'account' && <ProfileAccount empData={empData} />}
          {activeMenu === 'documents' && <ProfileDocuments empId={empId} docCategories={docCategories} documents={documents} setDocuments={setDocuments} showMsg={showMsg} baseUrl={baseUrl} />}
          
          {activeMenu === 'payroll' && (
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <ReceiptLongIcon sx={{ fontSize: 60, color: '#CBD5E1', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#64748B' }}>Payroll & Payslips</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 400, mx: 'auto' }}>
                Reserved for future updates. Your monthly payslips will appear here once generated.
              </Typography>
            </Box>
          )}

        </Paper>
      </Box>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.type} variant="filled" sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default MyProfile;